import type { ReactNode } from 'react';
import type { Messages } from './en';

/** Textes français : mêmes clés que en.tsx (vérifié par TypeScript). */
export const fr: Messages = {
  meta: {
    description: 'Pizzeria Fornavia — pizzas authentiques livrées avec passion. Commandez en ligne, livraison ou ramassage.',
    driverAppName: 'Fornavia Livreur',
  },
  common: {
    language: 'Langue',
    langNames: { en: 'English', fr: 'Français' },
    loading: 'Chargement',
    retry: 'Réessayer',
    close: 'Fermer',
    error: 'Erreur',
    httpError: (status: number) => `Erreur ${status}`,
    cancel: 'Annuler',
    save: 'Enregistrer',
    add: 'Ajouter',
    back: 'Retour',
    signOut: 'Déconnexion',
    online: 'En ligne',
    offline: 'Hors ligne',
    total: 'Total',
    subtotal: 'Sous-total',
    taxes: 'Taxes',
    tip: 'Pourboire',
    delivery: 'Livraison',
    pickup: 'Ramassage',
    deliveryFee: 'Frais de livraison',
    demoBadge: 'DÉMO',
    system: 'Système',
    items: (n: number) => `${n} article(s)`,
    order: (n: number | string) => `Commande #${n}`,
    payment: 'Paiement',
    refund: 'Remboursement',
    name: 'Nom',
    email: 'Courriel',
    phone: 'Téléphone',
    password: 'Mot de passe',
    address: 'Adresse',
    city: 'Ville',
    postalCode: 'Code postal',
    description: 'Description',
    closed: 'Fermé',
    open: 'Ouvert',
    closedToday: 'Fermé aujourd’hui',
    percent: (n: number) => `${n} %`,
    customer: 'Client',
    status: 'Statut',
    type: 'Type',
    driver: 'Livreur',
    driverLabel: 'Livreur : ',
    viewMenu: 'Voir le menu',
    publicSite: 'Site public',
    required: 'Requis',
    active: 'Actif',
    disabled: 'Désactivé',
    reconnecting: 'Reconnexion…',
  },
  app: {
    accessDenied: 'Accès refusé',
    noAccess: 'Votre rôle ne donne pas accès à cette page.',
    switchAccount: 'Changer de compte',
  },
  install: {
    button: 'Installer l’application',
    short: 'Installer',
    helpTitle: 'Ajouter à l’écran d’accueil',
    iosIntro: 'Sur iPhone et iPad, l’installation se fait depuis Safari :',
    iosStep1: (icon: ReactNode) => (
      <>
        Touchez {icon} <b>Partager</b> dans la barre de Safari.
      </>
    ),
    iosStep2: (icon: ReactNode) => (
      <>
        Choisissez {icon} <b>Sur l’écran d’accueil</b>.
      </>
    ),
    iosStep3: (appName: string) => (
      <>
        Touchez <b>Ajouter</b> : l’icône {appName} apparaît avec vos autres apps.
      </>
    ),
    otherIntro: 'Votre navigateur n’a pas encore proposé l’installation automatique.',
    otherStep1: (
      <>
        Ouvrez le menu du navigateur (<b>⋮</b> ou <b>…</b>).
      </>
    ),
    otherStep2: (
      <>
        Choisissez <b>Installer l’application</b> ou <b>Ajouter à l’écran d’accueil</b>.
      </>
    ),
    otherNote: 'Chrome, Edge et Samsung Internet affichent aussi une icône d’installation dans la barre d’adresse. L’installation exige une connexion HTTPS.',
    gotIt: 'Compris',
    pageTitle: (name: string) => `Installer ${name}`,
    perk1: 'Commande en deux touches depuis l’écran d’accueil',
    perk2: 'Suivi du livreur en direct sur la carte',
    perk3: 'Statut de la commande mis à jour automatiquement',
    alreadyInstalled: 'L’application est déjà installée sur cet appareil.',
  },
  pub: {
    restaurantNotFound: 'Restaurant introuvable',
    navHome: 'Accueil',
    navMenu: 'Menu',
    navAbout: 'À propos',
    navContact: 'Nous joindre',
    myAccount: 'Mon compte',
    signIn: 'Connexion',
    cart: 'Panier',
    openingHours: 'Heures d’ouverture',
    appTitle: 'Application',
    appPitch: (name: string) => `Installez ${name} sur votre téléphone pour commander en deux touches.`,
    staffArea: 'Espace employés',
    navigation: 'Navigation',
    orders: 'Commandes',
    account: 'Compte',
  },
  landing: {
    taglineFallback: 'Authentique. Frais. Délicieux.',
    orderMode: 'Mode de commande',
    heroAlt: 'Pizza Pepperoni',
    openNow: 'Ouvert maintenant',
    ordersAccepted: ' · commandes acceptées',
    deliveryEta: (min: number, max: number) => `Livraison ${min}–${max} min`,
    deliveryInfo: (fee: string, km: number) => `Frais ${fee} · rayon ${km} km`,
    orderNow: 'Commander maintenant',
    ourCategories: 'Nos catégories',
    fullMenu: 'Tout le menu',
    favourites: 'Les favoris',
    from: (price: string) => `À partir de ${price}`,
    features: [
      { t: 'Pizzas artisanales', d: 'Pâte maison, four à pierre.' },
      { t: 'Ingrédients frais', d: 'Produits locaux chaque jour.' },
      { t: 'Suivi en direct', d: 'Voyez votre livreur sur la carte.' },
      { t: 'Service 7/7', d: '' },
    ],
    viewCart: (amount: string) => `Voir le panier · ${amount}`,
  },
  menu: {
    categories: 'Catégories',
    searchPlaceholder: 'Rechercher un plat…',
    search: 'Rechercher',
    resultsFor: (q: string) => `Résultats pour « ${q} »`,
    ourCategory: (name: string | undefined) => `Nos ${name?.toLowerCase() ?? 'plats'}`,
    intro: 'Des recettes artisanales, des ingrédients frais, une pâte faite maison tous les jours.',
    allTags: 'Toutes',
    noMatch: 'Aucun plat ne correspond.',
    fromPrefix: 'À partir de ',
    addItem: (name: string) => `Ajouter ${name}`,
    add: 'Ajouter',
    stickyCart: (count: number, amount: string) => `Panier (${count}) · ${amount}`,
    yourOrder: 'Votre commande',
    yourOrderCount: (n: number) => `Votre commande (${n})`,
    clearAll: 'Tout effacer',
    emptyCart: 'Votre panier est vide. Ajoutez un plat pour commencer.',
    estimatedTotal: 'Total estimé',
    checkout: 'Passer à la caisse',
    removeOne: 'Retirer un',
    addOne: 'Ajouter un',
  },
  product: {
    added: (name: string) => `${name} ajouté au panier`,
    less: 'Moins',
    more: 'Plus',
    addFor: 'Ajouter — ',
    max: (n: number) => `Max ${n}`,
    optional: 'Optionnel',
    included: 'Inclus',
  },
  cart: {
    title: 'Votre panier',
    empty: 'Votre panier est vide.',
    taxesQc: 'Taxes (TPS + TVQ)',
    minimum: (amount: string) => `Commande minimum de ${amount}.`,
    keepShopping: 'Continuer mes achats',
  },
  checkout: {
    locationDenied: 'Position refusée: placez le repère sur la carte.',
    outOfZone: (km: string, max: number) => `Adresse hors zone (${km} km, maximum ${max} km).`,
    networkError: 'Erreur réseau, réessayez.',
    stepContact: 'Coordonnées',
    stepPayment: 'Paiement',
    receptionMode: 'Mode de réception',
    deliveryHint: 'On vient chez vous',
    pickupHint: 'Au restaurant',
    deliveryAddress: 'Adresse de livraison',
    addressPlaceholder: '1234 Rue des Lucioles',
    apartment: 'Appartement (optionnel)',
    instructions: 'Instructions (optionnel)',
    instructionsPlaceholder: 'Porte arrière, code…',
    pinPlaced: 'Repère placé sur la carte.',
    tapMap: 'Touchez la carte pour placer votre porte exactement.',
    distance: (km: string) => `Distance: ${km} km`,
    myLocation: 'Ma position',
    outOfZoneAlert: (max: number) => `Cette adresse est hors de notre zone de livraison (${max} km).`,
    pickupAtRestaurant: 'Ramassage au restaurant',
    readyIn: (min: number) => `Prête en environ ${min} minutes.`,
    fullName: 'Nom complet',
    kitchenNote: 'Note pour la cuisine (optionnel)',
    kitchenNotePlaceholder: 'Allergies, préférences…',
    edit: 'Modifier',
    tipTitle: 'Pourboire au livreur',
    noTip: 'Aucun',
    demoNotice: 'Mode démonstration : aucune carte réelle n’est débitée. Carte refusée de test : 4000 0000 0000 0002.',
    cardNumber: 'Numéro de carte',
    expiry: 'Expiration (MM/AA)',
    paying: 'Paiement…',
    payNow: (total: string) => `Payer maintenant · ${total}`,
    stripeReady: 'Paiement prêt pour Stripe · transaction sécurisée',
  },
  tracking: {
    steps: {
      PAID: { label: 'Commande reçue', desc: 'Votre commande a été confirmée.' },
      PREPARING: { label: 'En préparation', desc: 'Nos chefs préparent votre commande.' },
      READY: { label: 'Prête', desc: 'Votre commande est prête.' },
      READY_PICKUP: 'Venez la chercher au comptoir.',
      ASSIGNED: { label: 'Livreur assigné', desc: 'Un livreur a récupéré votre commande.' },
      OUT_FOR_DELIVERY: { label: 'En route', desc: 'Votre livreur est en chemin.' },
      DELIVERED: { label: 'Livrée', desc: 'Bon appétit !' },
      PICKED_UP: { label: 'Récupérée', desc: 'Merci et bon appétit !' },
    },
    yourPlace: 'Chez vous',
    notFound: 'Commande introuvable',
    placedAt: (time: string, restaurant: string) => `Passée à ${time} · ${restaurant}`,
    liveUpdates: 'Mises à jour en direct',
    finished: 'Terminé',
    live: 'En direct',
    reconnect: 'Reconnexion',
    cancelledMsg: (refunded: boolean, phone: string) =>
      `Cette commande a été ${refunded ? 'remboursée' : 'annulée'}. Contactez le restaurant au ${phone}.`,
    deliveredBy: (name: string) => `${name} vous a livré`,
    onTheWay: (name: string) => `${name} est en route`,
    etaIn: 'Arrivée estimée dans',
    waiting: 'En attente…',
    details: 'Détails',
    totalPaid: 'Total payé',
    deliveryTo: (addr: string) => `Livraison : ${addr}`,
    callRestaurant: 'Appeler le restaurant',
    newOrder: 'Nouvelle commande',
    ended: 'Livraison terminée : le suivi GPS est arrêté.',
    positionUpdated: (time: string) => `Position mise à jour ${time}`,
    showNumber: (n: number) => `Présentez le numéro #${n} au comptoir.`,
  },
  account: {
    hello: (name: string) => `Bonjour ${name}`,
    staffArea: 'Espace employé',
    signIn: 'Connexion',
    createAccount: 'Créer un compte',
    passwordMin: '(8 caractères minimum)',
    signInButton: 'Se connecter',
    createButton: 'Créer mon compte',
    demoAccount: 'Compte démo : customer@fornavia.example / Demo123!',
    myOrders: 'Mes commandes',
    track: 'Suivre',
    deviceOrders: 'Commandes passées depuis cet appareil :',
    orderFrom: (date: string) => `Commande du ${date}`,
    noOrders: 'Aucune commande pour l’instant.',
    appTitle: (name: string) => `L’application ${name}`,
    appPitch: 'Commandez plus vite depuis l’écran d’accueil de votre téléphone.',
  },
  login: {
    signInError: 'Erreur de connexion',
    platformTitle: 'Plateforme de livraison',
    platformPitch: 'Commandes, cuisine, répartition, livreurs et finances au même endroit, en temps réel.',
    platformFooter: 'Plusieurs restaurants, une seule plateforme.',
    title: 'Connexion',
    subtitle: 'Employés, livreurs et administrateurs',
    submit: 'Se connecter',
    demoAccounts: 'Comptes de DÉMONSTRATION (mot de passe Demo123!) :',
    backToSite: '← Retour au site du restaurant',
  },
  bo: {
    nav: {
      dashboard: 'Tableau de bord',
      orders: 'Commandes',
      kitchen: 'Cuisine (KDS)',
      dispatch: 'Répartition',
      menu: 'Menu',
      customers: 'Clients',
      employees: 'Employés',
      finance: 'Finances',
      settings: 'Paramètres',
    },
    noRestaurant: 'Aucun restaurant',
    noRestaurantText: 'Ce compte n’est employé d’aucun restaurant actif.',
    menuToggle: 'Menu',
    restaurant: 'Restaurant',
  },
  dashboard: {
    vsYesterday: (p: number) => `${p >= 0 ? `+${p}` : p} % vs hier`,
    ordersToday: 'Commandes aujourd’hui',
    netSales: 'Ventes nettes',
    activeDrivers: 'Livreurs actifs',
    outOf: (n: number) => `sur ${n}`,
    avgDelivery: 'Temps moyen de livraison',
    last7: '7 derniers jours',
    salesByHour: 'Ventes par heure (aujourd’hui)',
    hourLabel: (h: number) => `${h}h`,
    quickActions: 'Actions rapides',
    openKitchen: 'Ouvrir l’écran cuisine',
    viewDrivers: 'Voir les livreurs',
    manageMenu: 'Gérer le menu',
    salesReport: 'Rapport des ventes',
    newOrderSite: 'Nouvelle commande (site)',
    activeOrders: 'Commandes en cours',
    allOrders: 'Voir toutes les commandes →',
    time: 'Heure',
  },
  orders: {
    active: 'En cours',
    history: 'Historique',
    all: 'Toutes',
    allTypes: 'Tous les types',
    searchPlaceholder: 'Client, # ou téléphone',
    items: 'Articles',
    created: 'Créée',
    none: 'Aucune commande.',
    previous: 'Précédent',
    next: 'Suivant',
    page: (p: number, n: number) => `Page ${p} / ${n}`,
  },
  drawer: {
    events: { CREATED: 'Créée', PAID: 'Payée', UNASSIGNED: 'Livreur retiré' },
    refunded: 'Remboursement effectué',
    simulation: 'SIMULATION',
    note: (text: string) => `Note : ${text}`,
    driverQr: 'QR livreur',
    refund: 'Rembourser',
    cancel: 'Annuler',
    cancelTitle: (n: number) => `Annuler la commande #${n} ?`,
    reasonPlaceholder: 'Raison (visible dans l’historique)',
    keep: 'Garder',
    confirmCancel: 'Confirmer l’annulation',
    history: 'Historique',
    payments: 'Paiements',
    qrCreated: (time: string) => `QR : créé ${time}`,
    qrUsed: (time: string) => ` · utilisé ${time}`,
    qrRevoked: ' · révoqué',
    qrActive: ' · actif',
  },
  actions: {
    accept: 'Accepter',
    prepare: 'En préparation',
    ready: 'Prête',
    handOver: 'Remise au client',
    qrTitle: (n: number) => `QR de récupération · #${n}`,
    regenerate: 'Régénérer',
    print: 'Imprimer le reçu',
    orderLine: 'Commande',
    qrAlt: (n: number) => `QR commande ${n}`,
    qrHelp: (until: string | null) => `Le livreur scanne ce code dans son application. Code à usage unique${until ? `, valide jusqu’à ${until}` : ''}.`,
  },
  kitchen: {
    newOrder: (n: number) => `Nouvelle commande #${n}`,
    notifBody: (name: string, n: number) => `${name} · ${n} article(s)`,
    tabs: { all: 'Toutes', new: 'En attente', prep: 'En préparation', ready: 'Prêtes' },
    live: 'Temps réel',
    sound: 'Son',
    empty: 'Aucune commande dans cette colonne.',
    showQr: 'Afficher le QR',
  },
  dispatch: {
    demoSuffix: ' · DÉMO',
    assigned: 'Livreur assigné',
    unassigned: 'Assignation retirée',
    simStarted: (n: number) => `Simulation démarrée pour #${n}`,
    simStopped: 'Simulation arrêtée',
    filters: { all: 'Toutes', prep: 'En cuisine', ready: 'Prêtes', route: 'En route' },
    none: 'Aucune livraison.',
    assignDriver: 'Assigner un livreur',
    notAssigned: 'Non assignée',
    available: '· disponible',
    offline: '· hors ligne',
    seenAt: (time: string) => `· vu à ${time}`,
    waitingGps: '· en attente du GPS',
    stop: 'Arrêter',
    simulate: 'Simuler le trajet',
    onRoute: 'en route',
    driversAvailable: 'livreurs disponibles',
    toPickUp: 'à récupérer',
  },
  customers: {
    title: 'Clients',
    searchPlaceholder: 'Nom, courriel, téléphone',
    contact: 'Contact',
    orders: 'Commandes',
    spent: 'Total dépensé',
    lastOrder: 'Dernière commande',
    account: 'Compte',
    registered: 'Inscrit',
    guest: 'Invité',
    none: 'Aucun client.',
  },
  menuAdmin: {
    orderUpdated: 'Ordre mis à jour',
    categories: 'Catégories',
    moveUp: 'Monter',
    moveDown: 'Descendre',
    categoryShown: 'Catégorie visible',
    categoryHidden: 'Catégorie masquée',
    visible: 'Visible',
    newCategory: 'Nouvelle catégorie',
    categoryAdded: 'Catégorie ajoutée',
    products: 'Produits',
    addProduct: 'Ajouter un produit',
    noProducts: 'Aucun produit dans cette catégorie.',
    product: 'Produit',
    basePrice: 'Prix de base',
    options: 'Options',
    available: 'Disponible',
    productAvailable: 'Produit disponible',
    productOut: 'Produit en rupture',
    editItem: (name: string) => `Modifier ${name}`,
    imageUploaded: 'Image téléversée',
    saved: 'Produit enregistré',
    newProduct: 'Nouveau produit',
    basePriceField: 'Prix de base ($)',
    category: 'Catégorie',
    availableToOrder: 'Disponible à la commande',
    changeImage: 'Changer l’image',
    imageHint: 'PNG, JPG ou WEBP, 3 Mo maximum.',
    optionsTitle: 'Options et personnalisations',
    newGroupName: 'Nouvelle option',
    choice: (n: number) => `Choix ${n}`,
    optionGroup: 'Groupe d’options',
    groupName: 'Nom du groupe',
    single: 'Choix unique',
    multi: 'Choix multiples',
    deleteGroup: 'Supprimer le groupe',
    option: 'Option',
    extraCharge: 'Supplément',
    isDefault: 'défaut',
    remove: 'Retirer',
    addChoice: 'Ajouter un choix',
  },
  employees: {
    perms: {
      'dashboard.view': 'Tableau de bord',
      'orders.view': 'Commandes',
      'kitchen.view': 'KDS',
      'dispatch.view': 'Répartition',
      'menu.manage': 'Menu',
      'finance.view': 'Finances',
      'employees.manage': 'Employés',
      'settings.manage': 'Paramètres',
      'driver.deliver': 'Livraison',
    },
    nowRole: (name: string, role: string) => `${name} est maintenant ${role}`,
    accessDisabled: 'Accès désactivé',
    accessEnabled: 'Accès réactivé',
    title: (n: number) => `Employés (${n})`,
    add: 'Ajouter un employé',
    employee: 'Employé',
    role: 'Rôle',
    permissions: 'Permissions',
    since: 'Depuis',
    access: 'Accès',
    roleOf: (name: string) => `Rôle de ${name}`,
    disable: 'Désactiver',
    enable: 'Réactiver',
    audit: 'Journal d’audit',
    added: 'Employé ajouté',
    tempPassword: 'Mot de passe temporaire (8 caractères min.)',
  },
  finance: {
    periods: { today: 'Aujourd’hui', '7d': '7 jours', '30d': '30 jours', month: 'Ce mois', custom: 'Personnalisé' },
    from: 'Du',
    to: 'Au',
    exportCsv: 'Exporter CSV',
    netSales: 'Ventes nettes',
    ordersAvg: (n: number, avg: string) => `${n} commandes · moy. ${avg}`,
    taxes: 'Taxes collectées',
    tips: 'Pourboires',
    deliveryFees: 'Frais de livraison',
    refunded: (amount: string) => `− ${amount} remboursés`,
    byDay: 'Revenus par jour',
    breakdown: 'Répartition des revenus',
    food: 'Nourriture',
    split: (d: number, p: number) => `${d} livraisons · ${p} ramassages`,
    transactions: 'Transactions',
    fromDb: 'calculées depuis la base de données',
    date: 'Date',
    order: 'Commande',
    movement: 'Mouvement',
    amount: 'Montant',
    paid: 'Payé',
    declined: 'Refusé',
  },
  settings: {
    tabs: { info: 'Informations', hours: 'Horaires', zones: 'Zones de livraison', modes: 'Modes de service', payment: 'Paiement', notifications: 'Notifications' },
    saved: 'Paramètres enregistrés',
    imageUploaded: 'Image téléversée, pensez à enregistrer',
    restaurantName: 'Nom du restaurant',
    logo: 'Logo',
    changeLogo: 'Changer le logo',
    heroImage: 'Image d’accueil',
    changeImage: 'Changer l’image',
    promoTitle: 'Promotion de la page d’accueil',
    showBanner: 'Afficher la bannière',
    title: 'Titre',
    subtitle: 'Sous-titre',
    timezone: (tz: string) => `Fuseau horaire : ${tz}`,
    opening: (day: string) => `Ouverture ${day}`,
    closing: (day: string) => `Fermeture ${day}`,
    acceptWhenClosed: 'Accepter les commandes hors des heures d’ouverture (mode démo)',
    radius: 'Rayon de livraison (km)',
    fee: 'Frais de livraison ($)',
    minimum: 'Commande minimum ($)',
    mapHint: 'Touchez la carte pour déplacer le point de départ des livraisons (le restaurant).',
    homeDelivery: 'Livraison à domicile',
    pickupAtRestaurant: 'Ramassage au restaurant',
    prepTime: 'Temps de préparation moyen (min)',
    taxRate: 'Taux de taxes (%)',
    qrValidity: 'Validité du QR de récupération (min)',
    activeProvider: 'Fournisseur actif : ',
    simulatedPayment: 'Paiement simulé (démo)',
    stripeText: (a: ReactNode, b: ReactNode, c: ReactNode) => (
      <>
        La plateforme est prête pour Stripe : pour l’activer, l’administrateur du serveur définit {a}, {b} et {c}. Tant que ces variables ne sont
        pas définies, aucun appel n’est fait à Stripe.
      </>
    ),
    demoCards: 'Cartes de démonstration : 4242 4242 4242 4242 (acceptée), 4000 0000 0000 0002 (refusée).',
    kitchenSound: 'Son sur l’écran cuisine à chaque nouvelle commande',
    sound: 'Son',
    browserNotifs: 'Notifications du navigateur pour le personnel',
    browser: 'Navigateur',
    installTablet: 'Installer le back-office sur cette tablette :',
  },
  driver: {
    readyToast: 'Une commande est prête à récupérer',
    nav: { home: 'Accueil', orders: 'Commandes', history: 'Historique', profile: 'Profil' },
    toPickUpAria: (n: number) => `${n} à récupérer`,
    nowOffline: 'Vous êtes hors ligne',
    nowAvailable: 'Vous êtes disponible',
    hello: (name: string) => `Bonjour ${name} !`,
    available: 'Disponible',
    deliveriesToday: 'Livraisons aujourd’hui',
    earningsToday: 'Gains du jour',
    gpsPrefix: 'GPS : ',
    simulationMode: 'Mode simulation',
    realGpsMode: 'Mode GPS réel',
    deliveryInProgress: (n: number) => `Livraison en cours #${n}`,
    myOrders: 'Mes commandes',
    history: 'Historique',
    profileLink: 'Profil et permissions',
    scanCta: 'Scanner une commande',
    assignedOrders: 'Commandes assignées',
    toPickUp: (n: number) => `À récupérer (${n})`,
    inProgress: (n: number) => `En cours (${n})`,
    noneReady: 'Aucune commande prête pour le moment.',
    noneActive: 'Aucune livraison en cours.',
    assignedToYou: 'Assignée à vous',
    scanToPickUp: 'Scanner pour récupérer',
    viewDelivery: 'Voir la livraison',
    historyTotal: (n: number, amount: string) => `${n} livraisons · ${amount}`,
    noHistory: 'Aucune livraison terminée.',
    earningsHint: 'livraison + pourboire',
    perm: { granted: 'Autorisé', denied: 'Refusé', prompt: 'À demander', unknown: 'Inconnu' },
    roleAt: (restaurant: string) => `Livreur · ${restaurant}`,
    permissions: 'Permissions',
    location: 'Localisation : ',
    camera: 'Caméra : ',
    allow: 'Autoriser',
    permHelp: 'Si une permission est refusée, réactivez-la dans les réglages du navigateur pour ce site (icône de cadenas).',
    locationSource: 'Source de la position',
    realGps: 'GPS réel',
    simulation: 'Simulation (démo)',
    simulationHelp: 'La simulation fait avancer le livreur du restaurant jusqu’au client, pour une démonstration sans conduire.',
    gps: {
      idle: 'GPS en attente',
      requesting: 'Autorisation GPS…',
      active: 'GPS actif',
      denied: 'GPS refusé',
      unavailable: 'GPS indisponible',
      error: 'Signal GPS faible',
    },
    sendFailed: 'Envoi de position impossible',
    networkError: 'Erreur réseau',
    cameraDenied: 'Accès à la caméra refusé. Autorisez la caméra dans les réglages du navigateur, ou utilisez une photo.',
    cameraUnavailable: 'Caméra indisponible sur cet appareil. Utilisez une photo du QR.',
    noQrInPhoto: 'Aucun QR détecté sur la photo. Réessayez plus près du code.',
    scanTitle: 'Scanner le QR de la commande',
    pickedUp: (n: number) => `Commande #${n} récupérée`,
    pickedUpText: (name: ReactNode) => (
      <>
        Livraison pour <b>{name}</b>. Le suivi GPS démarre et le client voit votre position.
      </>
    ),
    startDelivery: 'Démarrer la livraison',
    cameraAria: 'Caméra',
    frameHint: 'Placez le QR dans le cadre',
    checking: 'Vérification…',
    searching: 'Recherche du QR…',
    openCamera: 'Ouvrir la caméra',
    takePhoto: 'Prendre une photo du QR',
    enterCode: 'Saisir le code',
    codePlaceholder: 'Code ou lien du QR',
    submit: 'Valider',
    deliveryNotFound: 'Livraison introuvable',
    delivered: (n: number) => `Commande #${n} livrée`,
    deliveredBadge: 'Livrée',
    outForDelivery: 'En livraison',
    arrival: (n: number) => `Arrivée ≈ ${n} min`,
    gpsDenied: 'La position est refusée : le client ne voit pas votre trajet. Autorisez la localisation dans le navigateur, ou passez en mode simulation dans votre profil.',
    restartSim: 'Relancer la simulation',
    callCustomer: 'Appeler le client',
    call: 'Appeler',
    paid: '(payée)',
    openMaps: 'Ouvrir dans Maps',
    finish: 'Terminer la livraison',
    confirmTitle: 'Confirmer la livraison',
    handedOver: 'Commande remise au client',
    confirmText: (n: number, name: string) => `Confirmez que la commande #${n} a été remise à ${name}. Le suivi GPS s’arrête pour le client.`,
  },
  platform: {
    superAdmin: 'Super-admin plateforme',
    restaurants: 'Restaurants',
    systemStatus: 'Statut système',
    activated: (name: string) => `${name} activé`,
    deactivated: (name: string) => `${name} désactivé`,
    restaurantsCount: (n: number) => `Restaurants (${n})`,
    restaurant: 'Restaurant',
    orders: 'Commandes',
    active: 'Actif',
    inProgress: 'En cours',
    netSales: 'Ventes nettes',
    employees: 'Employés',
    activate: (name: string) => `Activer ${name}`,
    open: 'Ouvrir',
    created: (date: string) => `créé ${date}`,
    deactivatedBadge: 'Désactivé',
    deactivate: 'Désactiver',
    activateBtn: 'Activer',
    menuItems: 'Produits au menu',
    team: 'Équipe',
    ordersByStatus: 'Commandes par statut',
    recentAudit: 'Audit récent',
    rows: {
      api: 'API',
      operational: 'Opérationnelle',
      started: 'Démarrée',
      database: 'Base de données',
      realtime: 'Connexions temps réel',
      payment: 'Paiement',
      paymentMock: 'Simulé (démo) — Stripe prêt, non connecté',
      driverPosition: 'Position livreurs',
      realGps: 'GPS réel (+ simulation disponible)',
      demo: 'Démo',
      simulator: 'Simulateur',
      simulatorOn: (n: number) => `Activé · ${n} trajet(s) en cours`,
      simulatorOff: 'Désactivé',
    },
    refresh: 'Actualiser',
    data: 'Données',
    migrations: 'Migrations',
    platformAudit: 'Audit plateforme',
  },
};
